import React, { useCallback, useEffect, useState } from 'react'
import { moneyString } from '../../../../utils'
import { Suborder, AllSubOrderCancellationResponse } from '@types'
import Loader from '@components/common/Loader'
import { YesNoPill } from './YesNoPill'
import { TruncatedCell } from './TruncatedCell'
import { ExpandableCell } from './ExpandableCell'

interface PreviewOrdersSectionProps {
  orderId: string
  onBack: () => void
  continueToNextSection: () => void
  cancelAndRefundAllSubOrders: (
    orderId: string,
  ) => Promise<AllSubOrderCancellationResponse>
  getGroupOrderActiveSubOrders: (groupOrderId: string) => Promise<Suborder[]>
}

export const PreviewOrdersSection: React.FC<PreviewOrdersSectionProps> = ({
  orderId,
  onBack,
  continueToNextSection,
  cancelAndRefundAllSubOrders,
  getGroupOrderActiveSubOrders,
}) => {
  const [orders, setOrders] = useState<Suborder[]>([])
  const [isLoadingOrders, setIsLoadingOrders] = useState(true)
  const [cancelResponse, setCancelResponse] = useState<
    AllSubOrderCancellationResponse | undefined
  >(undefined)
  const count = orders.length
  const [isSubmitting, setIsSubmitting] = useState(false)

  const fetchOrders = useCallback(async () => {
    setIsLoadingOrders(true)
    const orders = await getGroupOrderActiveSubOrders(orderId)
    setOrders(orders)
    setIsLoadingOrders(false)
  }, [orderId, getGroupOrderActiveSubOrders])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  const handleCancel = async () => {
    if (!count) {
      continueToNextSection()
    } else {
      setIsSubmitting(true)
      const cancelResp = await cancelAndRefundAllSubOrders(orderId)
      if (cancelResp) {
        setCancelResponse(cancelResp)
      }
      setIsSubmitting(false)
    }
  }

  const renderSections = () => {
    if (cancelResponse) {
      return (
        <SubOrderCancellationResults
          cancelResponse={cancelResponse}
          continueToNextSection={continueToNextSection}
          onGoToCancellationForm={() => {
            setCancelResponse(undefined)
            onBack()
          }}
          onGoToPreviewOrders={() => {
            setCancelResponse(undefined)
            fetchOrders()
          }}
        />
      )
    } else {
      return (
        <>
          <div>
            <h2 className="text-3xl font-semibold text-gray-900">
              Underlying Orders Need to Be Canceled: {count}{' '}
              {count === 1 ? 'order' : 'orders'}
            </h2>

            <p className="mt-4 text-lg text-gray-500 leading-relaxed max-w-6xl">
              Please review all the orders that need to be cancelled in order to
              change the status of this Group Order. Once processed, each
              impacted user will receive a cancellation email.
            </p>

            {/* Table container */}
            <div className="mt-8 rounded-2xl border border-gray-200 bg-white overflow-hidden">
              <div
                className="overflow-x-auto  overflow-y-auto"
                style={{ maxHeight: '400px' }}
              >
                <table className="min-w-[1200px] w-full">
                  <thead>
                    <tr className="text-left text-sm font-semibold text-gray-700 border-solid border-b border-gray-200">
                      <th className="px-6 py-4">Order Number</th>
                      <th className="px-6 py-4">Customer</th>
                      <th className="px-6 py-4">Items</th>
                      <th className="px-6 py-4 text-center">Subtotal</th>
                      <th className="px-6 py-4 text-center">Tax</th>
                      <th className="px-6 py-4 text-center">Tip</th>
                      <th className="px-6 py-4 text-center">Employee Paid</th>
                      <th className="px-6 py-4 text-center">Employor Paid</th>
                      <th className="px-6 py-4 text-center">Total</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {orders.map((o) => (
                      <tr
                        key={o.id}
                        className="text-sm text-gray-800  border-solid border-b border-gray-200"
                      >
                        <td
                          className="px-3 py-5 font-semibold text-gray-900 whitespace-no-wrap break-keep"
                          style={{ minWidth: '140px' }}
                        >
                          {o.orderNumber}
                        </td>

                        <td className="px-3 py-5">
                          <TruncatedCell
                            text={`${o.customer.firstName} ${o.customer.lastName}`}
                            className="font-medium text-gray-900"
                            maxWidth={180}
                          />
                          <TruncatedCell
                            text={o.customer.email}
                            className="text-gray-500"
                            maxWidth={180}
                          />
                        </td>

                        <td className="px-3 py-5" style={{ minWidth: '150px' }}>
                          <div className="space-y-1">
                            {o.menus.map((menu, mIdx) => (
                              <div key={mIdx} className="text-gray-800">
                                {menu.orderItems.map((it, idx) => (
                                  <TruncatedCell
                                    key={idx}
                                    text={`${it.quantity} X ${it.name}`}
                                    maxWidth={220}
                                  />
                                ))}
                              </div>
                            ))}
                          </div>
                        </td>

                        <td className="px-3 py-5 text-center tabular-nums">
                          ${moneyString(Number(o.subtotal))}
                        </td>
                        <td className="px-3 py-5 text-center tabular-nums">
                          ${moneyString(Number(o.tax))}
                        </td>
                        <td className="px-3 py-5 text-center tabular-nums">
                          ${moneyString(Number(o.tip))}
                        </td>
                        {/* User Paid*/}
                        <td className="px-3 py-5 text-center tabular-nums">
                          ${moneyString(Number(o.total))}
                        </td>
                        {/* Employor Paid (clientAmount + walletAmount) */}
                        <td className="px-3 py-5 text-center tabular-nums">
                          $
                          {moneyString(
                            Number(o.clientAmount) + Number(o.walletAmount),
                          )}
                        </td>
                        {/* Total Paid (user paid + employor paid) */}
                        <td className="px-3 py-5 text-center tabular-nums font-semibold text-gray-900">
                          $
                          {moneyString(
                            Number(o.total) +
                              Number(o.clientAmount) +
                              Number(o.walletAmount),
                          )}
                        </td>
                      </tr>
                    ))}

                    {orders.length === 0 && (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-6 py-10 text-center text-gray-500"
                        >
                          No underlying orders found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-10 flex items-center gap-6">
            {isSubmitting ? (
              <Loader />
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="flex-1 h-16 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:hover:bg-blue-500 text-white text-xl font-semibold transition-colors"
                >
                  {isSubmitting
                    ? 'PROCESSING...'
                    : orders.length === 0
                      ? 'No Orders - Proceed to Chef Pay Adjustments'
                      : 'Cancel & Notify Customers'}
                </button>

                <button
                  onClick={onBack}
                  disabled={isSubmitting}
                  className="flex-1 h-16 rounded-xl border border-gray-300 bg-white hover:bg-gray-300 disabled:opacity-60 text-gray-900 text-xl font-semibold transition-colors"
                >
                  Back
                </button>
              </>
            )}
          </div>
        </>
      )
    }
  }

  const content = isLoadingOrders ? <Loader /> : renderSections()

  return <div className="p-8">{content}</div>
}

interface SubOrderCancellationResultsProps {
  cancelResponse: AllSubOrderCancellationResponse

  continueToNextSection: () => void
  onGoToCancellationForm: () => void
  onGoToPreviewOrders: () => void
}

const SubOrderCancellationResults: React.FC<
  SubOrderCancellationResultsProps
> = ({
  cancelResponse,
  continueToNextSection,
  onGoToCancellationForm,
  onGoToPreviewOrders,
}) => {
  const normalizedOrders = cancelResponse.cancelledSubOrders
  const refundsNeeded = normalizedOrders.filter((s) => s.neededRefund).length
  const refundsSucceeded = normalizedOrders.filter(
    (s) => s.neededRefund && s.refundSucceeded,
  ).length
  const refundsFailed = normalizedOrders.filter(
    (s) => s.neededRefund && !s.refundSucceeded,
  ).length

  const hasCommitError = Boolean(cancelResponse.commitError)
  const hasEmailError = Boolean(cancelResponse.emailError)

  const { allOrdersCancelled } = cancelResponse

  return (
    <>
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        <h2 className="text-3xl font-semibold text-gray-900">
          Cancellation Results
        </h2>

        <p className="mt-4 text-lg text-gray-500 leading-relaxed max-w-6xl">
          Review the outcome of the cancellation and any required refunds.
        </p>
        {/* Summary cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            <div className="text-sm font-semibold text-gray-600">
              Cancellation Status
            </div>
            <div className="mt-3">
              <YesNoPill
                value={allOrdersCancelled}
                trueLabel="Success"
                falseLabel="Not fully cancelled"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            <div className="text-sm font-semibold text-gray-600">
              Changes Saved
            </div>
            <div className="mt-3">
              <YesNoPill
                value={!hasCommitError}
                trueLabel="Saved"
                falseLabel="Failed"
              />
            </div>
            {hasCommitError && (
              <div className="mt-3 text-sm text-red-700 break-words">
                {cancelResponse.commitError}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            <div className="text-sm font-semibold text-gray-600">
              Customer Notification
            </div>
            <div className="mt-3">
              <YesNoPill
                value={!hasEmailError}
                trueLabel="Sent"
                falseLabel="Failed"
              />
            </div>
            {hasEmailError && (
              <div className="mt-3 text-sm text-red-700 break-words">
                {cancelResponse.emailError}
              </div>
            )}
          </div>
        </div>

        {/* Refund quick stats */}
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6">
          <div className="flex flex-wrap gap-6 text-sm text-gray-700">
            <div>
              <span className="font-semibold text-gray-900">
                {normalizedOrders.length}
              </span>{' '}
              orders processed
            </div>
            <div>
              <span className="font-semibold text-gray-900">
                {refundsNeeded}
              </span>{' '}
              refunds needed
            </div>
            <div>
              <span className="font-semibold text-gray-900">
                {refundsSucceeded}
              </span>{' '}
              refunds succeeded
            </div>
            <div>
              <span className="font-semibold text-gray-900">
                {refundsFailed}
              </span>{' '}
              refunds failed
            </div>
          </div>
        </div>

        {/* Results table */}
        <div className="mt-8 rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <div className="max-h-[340px] overflow-y-auto">
              <table className="min-w-[1000px] w-full table-fixed">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-left text-sm font-semibold text-gray-700 border-b border-gray-200">
                    <th className="px-6 py-4 w-[160px] whitespace-nowrap">
                      Order #
                    </th>
                    <th className="px-6 py-4 w-[240px]">User</th>
                    <th className="px-6 py-4 w-[260px]">Email</th>
                    <th className="px-6 py-4 w-[160px] text-center whitespace-nowrap">
                      Refund Needed
                    </th>
                    <th className="px-6 py-4 w-[180px] text-center whitespace-nowrap">
                      Refund Succeeded
                    </th>
                    <th className="px-6 py-4">Refund Error</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {normalizedOrders.map((s) => (
                    <tr key={s.subOrderId} className="text-sm text-gray-800">
                      <td className="px-6 py-5 font-semibold text-gray-900 whitespace-nowrap">
                        {s.orderNumber}
                      </td>

                      <td className="px-6 py-5">
                        <TruncatedCell
                          text={s.userName}
                          className="font-medium text-gray-900"
                          maxWidth={200}
                        />
                      </td>

                      <td className="px-6 py-5">
                        <TruncatedCell
                          text={s.userEmail}
                          className="text-gray-700"
                          maxWidth={220}
                        />
                      </td>

                      <td className="px-6 py-5 text-center">
                        <YesNoPill value={s.neededRefund} />
                      </td>

                      <td className="px-6 py-5 text-center">
                        {s.neededRefund ? (
                          <YesNoPill
                            value={s.refundSucceeded}
                            trueLabel="Yes"
                            falseLabel="No"
                          />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      <td className="px-6 py-5">
                        {s.refundError ? (
                          <ExpandableCell
                            text={s.refundError}
                            className="text-red-700"
                            maxLength={150}
                          />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Actions - Fixed at bottom */}
      <div className="mt-10 flex items-center gap-6">
        {allOrdersCancelled ? (
          <button
            onClick={continueToNextSection}
            className="flex-1 h-16 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-xl font-semibold transition-colors disabled:opacity-60"
            disabled={!allOrdersCancelled}
          >
            Continue
          </button>
        ) : (
          <button
            onClick={onGoToPreviewOrders}
            className="flex-1 h-16 rounded-xl border border-gray-300 bg-white hover:bg-gray-300 text-gray-900 text-xl font-semibold transition-colors"
          >
            Back To Active Orders
          </button>
        )}

        <button
          onClick={onGoToCancellationForm}
          className="flex-1 h-16 rounded-xl border border-gray-300 bg-white hover:bg-gray-300 text-gray-900 text-xl font-semibold transition-colors"
        >
          {`Back ${allOrdersCancelled ? '' : ' To Cancellation Details'}`}
        </button>
      </div>
    </>
  )
}
